// How a new-account card is named, in ONE place.
//
// Ben's ruling, 2026-08-25: the FACILITY name is the primary line on every card, full
// stop. Everyone uses it — AMs, cleaners, Jose in conversation, inspections, My Work.
// The customer goes on a second, subordinate line, and ONLY when it differs from the
// facility name. Where they match, the caller shows "Single-site customer" rather than
// printing the same string twice. The customer is never promoted to the primary line.
//
// `card_title` is Trello residue and is deliberately NOT consulted. It was populated on
// 38 of 38 rows because the intake form auto-copied the account name into it, which is
// how `D`, `FSSP`, `AEBC` and `Day Porter` became card names.
//
// ⛔ `account_name` here means THE ACCOUNT'S OWN NAME, resolved through `account_id`.
// It is NOT `new_account_sop.account_name`, which is free text captured at intake and
// drifts the moment a building is reparented — 10 of 38 rows disagreed with their account
// on 2026-08-26 and this resolver faithfully printed the wrong one. Callers resolve first
// (use-sop-data.ts does it once for the whole module); this function only decides which of
// the two names goes on which line. TCSD-0118.
//
// The two SOP kanban views this used to mirror had no reader in src/ and were dropped
// on 2026-08-26 (TCSD-0109/0125). This module is the only answer now.

export type CardNameRow = {
  sop_id: string;
  facility_name?: string | null;
  account_name?: string | null;
};

export type CardName = {
  /** The facility. Always present — falls back to the account, then the id. */
  primary: string;
  /** The customer, only when it is not just the primary line again. */
  customer: string | null;
};

const clean = (v: string | null | undefined): string => (v ?? '').trim();

/**
 * Resolve the two lines of a new-account card's name.
 *
 * The fallback chain exists for rows that predate the site-first intake; a card created
 * through the current form always has a facility name.
 */
export function resolveCardName(row: CardNameRow): CardName {
  const facility = clean(row.facility_name);
  const account = clean(row.account_name);

  const primary = facility || account || row.sop_id;
  const customer = account && account !== primary ? account : null;

  return { primary, customer };
}

/** The primary line alone, for the many places that show a single string. */
export function cardDisplayName(row: CardNameRow): string {
  return resolveCardName(row).primary;
}

/**
 * What the subordinate line reads. A single-site customer is stated rather than
 * repeated — 68 of the 94 accounts that hold a facility hold exactly one, so this is
 * the common case and silence would read as missing data.
 */
export const SINGLE_SITE_CUSTOMER_LABEL = 'Single-site customer';

export function customerLineLabel(row: CardNameRow): string {
  return resolveCardName(row).customer ?? SINGLE_SITE_CUSTOMER_LABEL;
}
